'use client';

import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import InlineLoadingBar from './InlineLoadingBar/InlineLoadingBar';
import SecondaryConnectWalletButton from './SecondaryConnectWalletButton';
import { cm } from 'src/utils/classNames';
import { WithChildren } from 'src/utils/UtilityTypes';
import Button from 'src/components/primitives/Button/Button';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { PageRoute } from 'src/constants/constants';
import ConnectToTradeButton from 'src/components/ConnectToTradeButton';

export interface TableStateProps extends WithChildren {
	records: any[] | null;
	emptyStateText: React.ReactNode;
	requireWalletConnect?: boolean;
	customWalletConnectButton?: React.ReactNode;
	pageChangeLoading?: boolean;
	requireAccountCreated?: boolean;
	id?: string;
	showDepositButtonWhenEmpty?: boolean;
	handleClickDepositButton?: () => void;
	paginator?: React.ReactNode;
	alignTop?: boolean;
	className?: string;
	innerClassName?: string;
	loading?: boolean;
	style?: React.CSSProperties;
}

const TableStateWrapper = (props: TableStateProps) => {
	const connected = useWalletIsConnected();
	const userAccountNotInitialized = useDriftAccountStore(
		(s) => s.userAccountNotInitialized
	);
	const currentAccountExists = useDriftAccountStore(
		(s) => !!s.getCurrentUserAccount()
	);
	const currentRoute = useDriftStore((s) => s.currentPageRoute);
	const isTradePage = currentRoute === PageRoute.trade;

	const loadingState =
		connected &&
		!userAccountNotInitialized &&
		(props.records == undefined || props.loading);

	const emptyState =
		(props.requireAccountCreated && userAccountNotInitialized) ||
		((!props.requireWalletConnect || currentAccountExists) &&
			!!props.records &&
			props.records.length == 0 &&
			!props.pageChangeLoading);

	const content = (() => {
		if (connected && !currentAccountExists && props.requireAccountCreated) {
			return (
				<NoDataWrapper>
					<span className="typo-body-sm text-text-tertiary">
						No account found
					</span>
				</NoDataWrapper>
			);
		}

		if (props.requireWalletConnect && !connected) {
			return (
				<NoDataWrapper>
					{isTradePage ? (
						<ConnectToTradeButton />
					) : (
						<SecondaryConnectWalletButton />
					)}
				</NoDataWrapper>
			);
		}

		if (loadingState) {
			return (
				<NoDataWrapper>
					<InlineLoadingBar />
				</NoDataWrapper>
			);
		}

		if (emptyState) {
			return (
				<NoDataWrapper alignTop={props.alignTop}>
					<div className="my-8 flex flex-col items-center gap-y-md sm:my-0">
						<span className="typo-label-sm text-text-secondary">
							{props.emptyStateText}
						</span>
						{props.showDepositButtonWhenEmpty && (
							<Button
								size="sm"
								variant="secondary"
								onClick={props.handleClickDepositButton}
							>
								Deposit Assets
							</Button>
						)}
					</div>
				</NoDataWrapper>
			);
		}

		return props.children;
	})();

	return (
		<div
			id={props.id}
			className={cm('flex h-full grow flex-col not-sm:pb-lg', props.className)}
			style={props.style}
		>
			<div className="thin-scroll h-full overflow-auto">
				<div
					className={cm(
						'inline-block h-full min-w-full align-middle',
						props.innerClassName
					)}
				>
					{content}
				</div>
			</div>
			{props.paginator && (
				<div className="border-stroke-divider sm:border-t">
					{props.paginator}
				</div>
			)}
		</div>
	);
};

interface NoDataWrapperProps extends WithChildren {
	alignTop?: boolean;
	className?: string;
}

function NoDataWrapper({ children, alignTop, className }: NoDataWrapperProps) {
	return (
		<div
			className={cm(
				'flex size-full flex-col items-center justify-center sm:rounded-b-sm sm:bg-container-bg',
				'min-h-40 sm:min-h-80',
				alignTop && 'justify-start pt-6',
				className
			)}
		>
			{children}
		</div>
	);
}

export default TableStateWrapper;
